import { getCardPrimaryType, TYPE_ORDER } from './deckTypeUtils';

const getCardCmc = (card) => {
  if (card.cmc !== undefined && card.cmc !== null) {
    return Math.floor(card.cmc);
  }
  if (card.card_faces && card.card_faces.length > 0) {
    const firstFaceCmc = card.card_faces[0].cmc;
    if (firstFaceCmc !== undefined && firstFaceCmc !== null) {
      return Math.floor(firstFaceCmc);
    }
  }
  return 0;
};

const SORT_OPTIONS = [
  { value: 'type', label: 'By Type' },
  { value: 'mana_cost', label: 'By Mana Cost' },
  { value: 'color', label: 'By Color' },
  { value: 'rarity', label: 'By Rarity' },
  { value: 'name', label: 'By Name' },
];


const sortByType = (cards) => {
  const groups = {};
  TYPE_ORDER.forEach((type) => { groups[type] = []; });

  cards.forEach((entry) => {
    const type = getCardPrimaryType(entry.type_line);
    groups[type].push(entry);
  });

  return TYPE_ORDER
    .filter((type) => groups[type].length > 0)
    .map((type) => ({
      sortKey: type,
      label: type === 'Other' ? 'Other' : type + 's',
      cards: groups[type],
      totalCount: groups[type].reduce((sum, e) => sum + (e.quantity || 1), 0),
    }));
};

const sortByManaCost = (cards) => {
  const buckets = {};
  const landBucket = [];

  for (let i = 0; i <= 8; i++) {
    buckets[i === 8 ? '8+' : i.toString()] = [];
  }

  cards.forEach((entry) => {
    const isLand = getCardPrimaryType(entry.type_line) === 'Land';
    if (isLand) {
      landBucket.push(entry);
      return;
    }
    const cmc = getCardCmc(entry);
    const key = cmc >= 8 ? '8+' : cmc.toString();
    buckets[key].push(entry);
  });

  const keys = ['0', '1', '2', '3', '4', '5', '6', '7', '8+'];
  const result = keys
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({
      sortKey: k,
      label: `CMC ${k}`,
      cards: buckets[k],
      totalCount: buckets[k].reduce((sum, e) => sum + (e.quantity || 1), 0),
    }));

  if (landBucket.length > 0) {
    result.push({
      sortKey: 'Land',
      label: 'Lands',
      cards: landBucket,
      totalCount: landBucket.reduce((sum, e) => sum + (e.quantity || 1), 0),
    });
  }

  return result;
};

const sortByColor = (cards) => {
  const groups = {};

  cards.forEach((entry) => {
    const isLand = getCardPrimaryType(entry.type_line) === 'Land';
    let bucket;

    if (isLand) {
      bucket = 'Lands';
    } else {
      const raw = entry.colors_raw || [];
      if (raw.length === 0) bucket = 'Colorless';
      else if (raw.length === 1) bucket = raw[0];
      else bucket = 'Multicolor';
    }

    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(entry);
  });

  const colorOrder = ['W', 'U', 'B', 'R', 'G', 'Colorless', 'Multicolor', 'Lands'];
  return colorOrder
    .filter((color) => groups[color])
    .map((color) => ({
      sortKey: color,
      label: color,
      cards: groups[color],
      totalCount: groups[color].reduce((sum, e) => sum + (e.quantity || 1), 0),
    }));
};

const sortByRarity = (cards) => {
  const groups = {};

  cards.forEach((entry) => {
    const rarity = entry.rarity ? (entry.rarity.charAt(0).toUpperCase() + entry.rarity.slice(1).toLowerCase()) : 'Unknown';
    if (!groups[rarity]) groups[rarity] = [];
    groups[rarity].push(entry);
  });

  const rarityOrder = ['Mythic', 'Rare', 'Uncommon', 'Common', 'Unknown'];
  return rarityOrder
    .filter((r) => groups[r])
    .map((r) => ({
      sortKey: r,
      label: r,
      cards: groups[r],
      totalCount: groups[r].reduce((sum, e) => sum + (e.quantity || 1), 0),
    }));
};

const sortByName = (cards) => {
  const sorted = [...cards].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return [{
    sortKey: 'all',
    label: 'Cards',
    cards: sorted,
    totalCount: sorted.reduce((sum, e) => sum + (e.quantity || 1), 0),
  }];
};

const sortByCmc = (cards) => {
  const sorted = [...cards].sort((a, b) => {
    const cmcA = getCardCmc(a);
    const cmcB = getCardCmc(b);
    if (cmcA !== cmcB) return cmcA - cmcB;
    return (a.name || '').localeCompare(b.name || '');
  });
  return [{
    sortKey: 'all',
    label: 'Cards',
    cards: sorted,
    totalCount: sorted.reduce((sum, e) => sum + (e.quantity || 1), 0),
  }];
};

const groupLandsTogether = (groups) => {
  const landGroup = groups.find((g) => g.sortKey === 'Land');
  if (!landGroup) return groups;

  return [...groups.filter((g) => g.sortKey !== 'Land'), landGroup];
};

const sortAndGroupCards = (cards = [], sortBy = 'type') => {
  if (!Array.isArray(cards)) {
    console.warn('sortAndGroupCards: cards must be an array, received', typeof cards);
    return [];
  }

  let groups;

  switch (sortBy) {
    case 'mana_cost':
      groups = sortByManaCost(cards);
      break;
    case 'color':
      groups = sortByColor(cards);
      break;
    case 'rarity':
      groups = sortByRarity(cards);
      break;
    case 'name':
      groups = sortByName(cards);
      break;
    case 'cmc':
      groups = sortByCmc(cards);
      break;
    case 'type':
    default:
      groups = sortByType(cards);
      break;
  }

  if (sortBy === 'type') {
    groups = groupLandsTogether(groups);
  }

  return groups;
};

export {
  SORT_OPTIONS,
  sortAndGroupCards,
};
